/**
 * Manage add-ons on one booking, from the Billing workbench (moved off the client page 16 Sep 2026,
 * Trera: booking edits live on Billing with the other billing actions).
 *
 * Record an add-on handled outside the normal purchase flow, or remove one that should not be here.
 * Nothing here charges a card. The server route (/api/admin/addons) does the ledger work: the row,
 * the agreed total, the payment row when already paid, hours, mirroring and notifications.
 *
 * 21 Sep 2026: when the booking is overpaid (booking_balances.overpaid_cents), the dialog says so and offers
 * "Use the client's credit" first, so an outside payment nobody recorded becomes the add-on it paid for
 * instead of being recorded a second time (Stephanie Fykes: -$395 stayed -$395 after "Already paid").
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/admin/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/admin/ui/dialog';
import { Input } from '@/components/admin/ui/input';
import { Label } from '@/components/admin/ui/label';
import { Textarea } from '@/components/admin/ui/textarea';
import { Loader2, PlusCircle, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { isVideoAddon } from '@/lib/addons/addonFamilies';

type Billing = 'paid_outside' | 'apply_credit' | 'add_to_balance' | 'complimentary';
type NewAddon = { addonId: string; quantity: string; unitPrice: string; coverageHours: string; billing: Billing; paymentReference: string; notes: string; confirmNewMoney: boolean };
const EMPTY: NewAddon = { addonId: '', quantity: '1', unitPrice: '', coverageHours: '', billing: 'paid_outside', paymentReference: '', notes: '', confirmNewMoney: false };
const dollars = (c: number) => `$${(Math.abs(Number(c) || 0) / 100).toFixed(2)}`;

async function adminApi(path: string, init: RequestInit = {}) {
  const { data: { session } } = await supabaseAdmin.auth.getSession();
  if (!session) throw new Error('You must be logged in to perform this action.');
  const res = await fetch(path, { ...init, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}`, ...(init.headers || {}) } });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || json.error || `Request failed (${res.status})`);
  return json;
}

type Props = { bookingId: string; open: boolean; onClose: () => void; onChanged?: () => void };
type Fees = { applied_cents: number; travel_cents: number; mileage_cents: number; late_booking_cents: number };

/**
 * Fees (Traci, 28 Sep 2026): late fee $25 and failed payment fee $15 go on the booking's fees; travel, mileage and the
 * late booking fee ($450 per team member; Kendra, 29 Oct 2026) are set to an amount. Not add-ons. Rules: src/lib/billing/bookingFees.js; Admin and Billing only (the section hides
 * for anyone else). Nothing charges a card.
 */
function FeesSection({ bookingId, onChanged }: { bookingId: string; onChanged?: () => void }) {
  const [fees, setFees] = useState<Fees | null>(null);
  const [reason, setReason] = useState('');
  const [travel, setTravel] = useState('');
  const [mileage, setMileage] = useState('');
  const [lateBooking, setLateBooking] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => adminApi(`/api/admin/billing/bookings/${bookingId}/fees`).then((j) => {
    setFees(j.fees); setTravel((j.fees.travel_cents / 100).toString()); setMileage((j.fees.mileage_cents / 100).toString());
    setLateBooking((j.fees.late_booking_cents / 100).toString());
  }).catch(() => setFees(null));
  useEffect(() => { load(); }, [bookingId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!fees) return null;
  const send = async (body: Record<string, unknown>) => {
    if (reason.trim().length < 4) { toast.error('Write the reason first'); return; }
    setBusy(true);
    try {
      const j = await adminApi(`/api/admin/billing/bookings/${bookingId}/fees`, { method: 'POST', body: JSON.stringify({ ...body, reason: reason.trim() }) });
      toast.success(`${j.note.split('. Reason:')[0]}.${j.invoice_note ? ` ${j.invoice_note}` : ''}`);
      setReason('');
      await load();
      onChanged?.();
    } catch (err: any) { toast.error(err.message); } finally { setBusy(false); }
  };
  const cents = (v: string) => Math.round((parseFloat(v || '0') || 0) * 100);
  return (
    <div className="space-y-3 border-t pt-4">
      <p className="text-sm font-medium">Fees</p>
      <p className="text-xs text-muted-foreground">Late and failed payment fees on this booking: <strong>{dollars(fees.applied_cents)}</strong>. Nothing here charges a card; it changes what the client owes.</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => send({ fee: 'late_payment', action: 'add' })}>+ Late fee $25</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => send({ fee: 'failed_payment', action: 'add' })}>+ Failed payment fee $15</Button>
        <Button size="sm" variant="ghost" disabled={busy || fees.applied_cents < 2500} onClick={() => send({ fee: 'late_payment', action: 'waive' })}>Waive $25</Button>
        <Button size="sm" variant="ghost" disabled={busy || fees.applied_cents < 1500} onClick={() => send({ fee: 'failed_payment', action: 'waive' })}>Waive $15</Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="fee-travel">Travel fee ($)</Label>
          <div className="flex gap-2"><Input id="fee-travel" type="number" min="0" step="0.01" value={travel} onChange={(e) => setTravel(e.target.value)} />
            <Button size="sm" variant="outline" disabled={busy || cents(travel) === fees.travel_cents} onClick={() => send({ fee: 'travel', amount_cents: cents(travel) })}>Save</Button></div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="fee-mileage">Mileage fee ($)</Label>
          <div className="flex gap-2"><Input id="fee-mileage" type="number" min="0" step="0.01" value={mileage} onChange={(e) => setMileage(e.target.value)} />
            <Button size="sm" variant="outline" disabled={busy || cents(mileage) === fees.mileage_cents} onClick={() => send({ fee: 'mileage', amount_cents: cents(mileage) })}>Save</Button></div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="fee-late-booking">Late booking fee ($)</Label>
          <div className="flex gap-2"><Input id="fee-late-booking" type="number" min="0" step="0.01" placeholder="450 per team member" value={lateBooking} onChange={(e) => setLateBooking(e.target.value)} />
            <Button size="sm" variant="outline" disabled={busy || cents(lateBooking) === fees.late_booking_cents} onClick={() => send({ fee: 'late_booking', amount_cents: cents(lateBooking) })}>Save</Button></div>
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="fee-reason">Reason (required for any fee change)</Label>
        <Input id="fee-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. final balance 10 days late" />
      </div>
    </div>
  );
}

export default function ManageAddonsDialog({ bookingId, open, onClose, onChanged }: Props) {
  const [rows, setRows] = useState<any[]>([]);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<NewAddon>(EMPTY);
  const [overpaidCents, setOverpaidCents] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, b] = await Promise.all([
        supabaseAdmin.from('booking_addons').select('id, quantity, unit_price_cents, total_price_cents, payment_status, purchased_at, notes, addon:addons(id, code, name, description)').eq('booking_id', bookingId).order('purchased_at', { ascending: false }),
        catalog.length ? Promise.resolve({ addons: catalog }) : adminApi('/api/admin/addons/catalog'),
        supabaseAdmin.from('booking_balances').select('overpaid_cents').eq('booking_id', bookingId).maybeSingle()
      ]);
      if (r.error) throw r.error;
      setRows(r.data || []);
      setCatalog(c.addons || []);
      const credit = Math.max(0, Math.round(Number((b.data as any)?.overpaid_cents || 0))); // view rev 3 column; generated types predate it
      setOverpaidCents(credit);
      // With money already sitting above the agreed price, the likeliest truth is that it bought something.
      setDraft((d) => (credit > 0 && d.billing === 'paid_outside' ? { ...d, billing: 'apply_credit' } : d));
    } catch (err: any) {
      toast.error(`Could not load add-ons: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && bookingId) { setDraft(EMPTY); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  const selected = catalog.find((a) => a.id === draft.addonId) || null;
  const selectedIsVideo = !!selected && isVideoAddon(selected.code);
  const draftTotalCents = draft.billing === 'complimentary' ? 0 : Math.round((parseFloat(draft.unitPrice || '0') || 0) * 100) * Math.max(1, parseInt(draft.quantity || '1', 10) || 1);
  const creditCoversDraft = overpaidCents > 0 && draftTotalCents > 0 && overpaidCents >= draftTotalCents;

  const add = async () => {
    if (!draft.addonId) { toast.error('Pick an add-on first'); return; }
    setSaving(true);
    try {
      const unitPriceCents = draft.unitPrice !== '' ? Math.round(parseFloat(draft.unitPrice) * 100) : (selected?.price_cents ?? 0);
      const json = await adminApi(`/api/admin/addons/bookings/${bookingId}`, {
        method: 'POST',
        body: JSON.stringify({
          addonId: draft.addonId,
          quantity: Math.max(1, parseInt(draft.quantity || '1', 10) || 1),
          unitPriceCents,
          coverageHours: selectedIsVideo && draft.coverageHours ? Number(draft.coverageHours) : null,
          billing: draft.billing,
          paymentReference: draft.paymentReference,
          notes: draft.notes,
          confirmNewMoney: draft.billing === 'paid_outside' && draft.confirmNewMoney
        })
      });
      const money = json.creditAppliedCents
        ? ` · ${dollars(json.creditAppliedCents)} of the client's credit applied${json.overpaidAfterCents > 0 ? `, ${dollars(json.overpaidAfterCents)} still overpaid` : ', ledger is level'}`
        : '';
      toast.success(`${selected?.name || 'Add-on'} added${json.totalCents ? ` (${dollars(json.totalCents)})` : ' (complimentary)'}${money}${json.bonusHours ? ` · +${json.bonusHours} hour${json.bonusHours === 1 ? '' : 's'}` : ''}.${json.invoiceNote ? ` ${json.invoiceNote}` : ''}`);
      setDraft(EMPTY);
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error(`Could not add add-on: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: any) => {
    const label = row.addon?.name || 'this add-on';
    const reason = window.prompt(`Remove ${label} from this booking? Enter a reason (required):`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setRemovingId(row.id);
    try {
      const json = await adminApi(`/api/admin/addons/bookings/${bookingId}/rows/${row.id}`, { method: 'DELETE', body: JSON.stringify({ reason: reason.trim() }) });
      toast.success(`${json.refundDueCents
        ? `${label} removed. The client already paid $${(json.refundDueCents / 100).toFixed(2)} for it. Refund that in Stripe if it is owed back.`
        : `${label} removed.`}${json.invoiceNote ? ` ${json.invoiceNote}` : ''}`);
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error(`Could not remove add-on: ${err.message}`);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage add-ons</DialogTitle>
          <DialogDescription>
            Record an add-on handled outside the normal purchase flow (including a date change), or remove one that should not be here. Nothing here charges a card.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <p className="text-sm font-medium mb-2">On this booking</p>
            {loading && rows.length === 0 ? <p className="text-sm text-muted-foreground">Loading…</p>
              : rows.length === 0 ? <p className="text-sm text-muted-foreground">No post-booking add-ons recorded.</p>
              : (
                <div className="space-y-2">
                  {rows.map((row: any) => (
                    <div key={row.id} className="flex items-center justify-between rounded border p-2 text-sm">
                      <div>
                        <span className="font-medium">{row.addon?.name || 'Add-on'}</span>
                        <span className="text-muted-foreground"> ×{row.quantity} · ${(Number(row.total_price_cents || 0) / 100).toFixed(2)} · {row.payment_status}</span>
                        {row.notes && <span className="block text-xs text-muted-foreground">{row.notes}</span>}
                      </div>
                      <Button variant="ghost" size="sm" disabled={removingId === row.id} onClick={() => remove(row)} aria-label={`Remove ${row.addon?.name || 'add-on'}`}>
                        {removingId === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-destructive" />}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
          </div>
          {open && <FeesSection bookingId={bookingId} onChanged={onChanged} />}
          <div className="space-y-3 border-t pt-4">
            <p className="text-sm font-medium">Add an add-on</p>
            {overpaidCents > 0 && (
              <p className="rounded border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900" role="status">
                This client has paid <strong>{dollars(overpaidCents)}</strong> more than the agreed price. Most often that is an add-on bought outside the site that nobody recorded. Pick the add-on and choose <strong>Use the client&apos;s credit</strong> so the ledger goes level. Only pick <strong>Already paid</strong> if new money came in on top of that credit.
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="billing-addon-pick">Add-on *</Label>
              <select id="billing-addon-pick" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={draft.addonId}
                onChange={(e) => { const a = catalog.find((x) => x.id === e.target.value); setDraft({ ...draft, addonId: e.target.value, unitPrice: a ? (Number(a.price_cents || 0) / 100).toString() : '' }); }}>
                <option value="">Select an add-on…</option>
                {catalog.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} — ${(Number(a.price_cents || 0) / 100).toFixed(2)}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="billing-addon-qty">Qty</Label>
                <Input id="billing-addon-qty" type="number" min="1" step="1" value={draft.quantity} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-addon-price">Unit price ($)</Label>
                <Input id="billing-addon-price" type="number" min="0" step="0.01" disabled={draft.billing === 'complimentary'} value={draft.billing === 'complimentary' ? '0' : draft.unitPrice} onChange={(e) => setDraft({ ...draft, unitPrice: e.target.value })} />
              </div>
              {selectedIsVideo && (
                <div className="space-y-2">
                  <Label htmlFor="billing-addon-hours">Video hours</Label>
                  <Input id="billing-addon-hours" type="number" min="1" step="1" value={draft.coverageHours} onChange={(e) => setDraft({ ...draft, coverageHours: e.target.value })} />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing-addon-billing">How was it paid? *</Label>
              <select id="billing-addon-billing" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={draft.billing}
                onChange={(e) => setDraft({ ...draft, billing: e.target.value as Billing })}>
                {overpaidCents > 0 && <option value="apply_credit">Use the client&apos;s credit ({dollars(overpaidCents)} already paid, nothing recorded for it)</option>}
                <option value="paid_outside">Already paid (Stripe dashboard, check, other) — new money, not yet on the ledger</option>
                <option value="add_to_balance">Add to the client&apos;s balance (they still owe it)</option>
                <option value="complimentary">Complimentary ($0)</option>
              </select>
              <p className="text-xs text-muted-foreground">
                {draft.billing === 'apply_credit'
                  ? (draftTotalCents === 0
                      ? 'Raises the agreed total by the add-on and uses the overpayment to cover it. No new payment is recorded.'
                      : creditCoversDraft
                        ? `Raises the agreed total by ${dollars(draftTotalCents)} and covers it from the ${dollars(overpaidCents)} credit${overpaidCents - draftTotalCents > 0 ? `; ${dollars(overpaidCents - draftTotalCents)} stays overpaid` : '; the ledger goes level'}.`
                        : `The credit is ${dollars(overpaidCents)} and this add-on is ${dollars(draftTotalCents)}. Lower the unit price to the credit, or use Already paid and confirm the rest was paid separately.`)
                  : draft.billing === 'paid_outside'
                    ? 'Raises the agreed total and records a new payment for it, so the ledger stays level.'
                    : draft.billing === 'add_to_balance'
                      ? 'Raises the agreed total; the client sees the amount owed in their portal.'
                      : 'Recorded at $0. Hours and staffing still apply (e.g. Hour on Us adds an hour).'}
              </p>
            </div>
            {draft.billing === 'paid_outside' && (
              <div className="space-y-2">
                <Label htmlFor="billing-addon-ref">Payment reference (Stripe id, check #…)</Label>
                <Input id="billing-addon-ref" value={draft.paymentReference} onChange={(e) => setDraft({ ...draft, paymentReference: e.target.value })} placeholder="pi_… / ch_… / check 1042" />
                {overpaidCents > 0 && (
                  <label className="flex items-start gap-2 text-xs text-amber-900">
                    <input type="checkbox" className="mt-0.5" checked={draft.confirmNewMoney} onChange={(e) => setDraft({ ...draft, confirmNewMoney: e.target.checked })} />
                    <span>This is new money, separate from the {dollars(overpaidCents)} already showing as overpaid. (If it is that money, pick Use the client&apos;s credit instead.)</span>
                  </label>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="billing-addon-notes">Notes</Label>
              <Textarea id="billing-addon-notes" rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Why this is being recorded" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={add} disabled={saving || !draft.addonId || (draft.billing === 'apply_credit' && draftTotalCents > 0 && !creditCoversDraft) || (draft.billing === 'paid_outside' && overpaidCents > 0 && !draft.confirmNewMoney)}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PlusCircle className="h-4 w-4 mr-2" />}
            Add to booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
